import Swiper, { EffectFade, Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';

function setProgress(container, numberOfSteps, activeStep) {
    const scale = container.getBoundingClientRect().width / container.getBBox().width;
    const circumference = container.getTotalLength() * scale;

    container.style.strokeDasharray = `${(circumference / numberOfSteps) * activeStep} ${(circumference - (circumference / numberOfSteps) * activeStep)}`;
    container.style.strokeDashoffset = circumference / 4;
}

function resetProgress(container) {
    const scale = container.getBoundingClientRect().width / container.getBBox().width;
    const circumference = container.getTotalLength() * scale;

    container.style.strokeDasharray = `0 ${(circumference)}`;
}

// Helper to set the height of the slides to the tallest
function equalizeSlideHeights(container) {
    const slides = container.querySelectorAll('.swiper-slide');
    let maxHeight = 0;

    // reset
    slides.forEach(slide => (slide.style.height = 'auto'));

    // find tallest
    slides.forEach(slide => {
        maxHeight = Math.max(maxHeight, slide.offsetHeight);
    });

    // apply tallest
    slides.forEach(slide => {
        slide.style.height = maxHeight + 'px';
    });
}

export class StatsCarouselAnimation {
    constructor (element) {
        if (!element) return ;

        this.element = element;
        this.circle = this.element.querySelector('circle');
        this.numberOfSteps = Number(this.element.dataset.jsStatsCarouselAnimationStepCount);
        this.init();
    }

    init () {
        this.setProgressPoints();
    }

    /**
     * Plots the circular progress points on top of the `<circle>`
     */
    setProgressPoints () {
        const containerRadius = Number(this.circle.getAttribute('r'));
        const progressPointOuterRadius = 10;
        const progressPointInnerRadius = 4;
        const cx = Number(this.circle.getAttribute('cx'));
        const cy = Number(this.circle.getAttribute('cy'));

        for (let i = 0; i < this.numberOfSteps; i ++) {
            let angle = (-Math.PI / 2) + (i / this.numberOfSteps) * 2 * Math.PI;
            let x = cx + containerRadius * Math.cos(angle);
            let y = cy + containerRadius * Math.sin(angle);

            const progressPointOuter = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            progressPointOuter.setAttribute('cx', x);
            progressPointOuter.setAttribute('cy', y);
            progressPointOuter.setAttribute('r', progressPointOuterRadius);
            progressPointOuter.setAttribute('stroke', '#FEFCF7');
            progressPointOuter.setAttribute('stroke-opacity', 0);
            progressPointOuter.setAttribute('fill', 'none');
            progressPointOuter.setAttribute('style', 'transition: stroke-opacity 0.1s linear');

            const progressPointInner = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            progressPointInner.setAttribute('cx', x);
            progressPointInner.setAttribute('cy', y);
            progressPointInner.setAttribute('r', progressPointInnerRadius);
            progressPointInner.setAttribute('fill', '#FEFCF7');

            this.element.querySelector('.js-stats-carousel-animation-progress-point-outer-container').appendChild(progressPointOuter);
            this.element.querySelector('.js-stats-carousel-animation-progress-point-inner-container').appendChild(progressPointInner);
        }
    }
}

export class StatsCarouselCarousel {
    constructor (element) {
        if (!element) return;

        this.element = element;
        this.init();
    }

    init () {
        const paginationConfig = JSON.parse(this.element.querySelector('.js-stats-carousel-carousel-controls-config').textContent);

        new Swiper(this.element, {
            modules: [EffectFade, Navigation, Pagination],
            slidesPerView: 'auto',
            threshold: 10,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            navigation: {
                prevEl: this.element.querySelector('.swiper-button-prev'),
                nextEl: this.element.querySelector('.swiper-button-next')
            },
            pagination: {
                el: this.element.querySelector('.swiper-pagination'),
                clickable: true,
                renderBullet(index, className) {
                    return `<li class="${className} flex items-center">
                                <button
                                    class="border border-[#FEFCF7] rounded-full w-2 h-2 transition-colors duration-300 ease-in-out [.swiper-pagination-bullet-active_&]:bg-[#FEFCF7]"
                                    type="button"
                                    aria-label="${paginationConfig[index].name}"
                                    aria-controls="${paginationConfig[index].controlsId}"
                                >
                                </button>
                            </li>`;
                }
            },
            loop: true,
            on: {
                init: () => {
                    equalizeSlideHeights(this.element); 
                },
                resize: () => {
                    equalizeSlideHeights(this.element);
                },
                slideChange: swiper => {
                    const context = this.element.closest('.js-stats-carousel');
                    const animationCircle = context.querySelector('.js-stats-carousel-animation circle');
                    const animationCircleContainer = animationCircle.closest('svg');
                    const slidesCount = Number(context.querySelector('.js-stats-carousel-animation').dataset.jsStatsCarouselAnimationStepCount);
                    const initialTransitionDuration = Number(window.getComputedStyle(animationCircle).transitionDuration.replace('s', ''));

                    // Changes the progress of the `stroke-dasharray` on the `<circle>`
                    // `scaleX(-1)` is a quick hack to temporarily flip the SVG so `stroke-dasharray` appears to reverse.
                    if (swiper.activeIndex === slidesCount + 1) {
                        animationCircleContainer.style.transform = null;
                        animationCircle.style.transitionDuration = `${initialTransitionDuration / 2}s`;
                        animationCircleContainer.style.transform = 'scaleX(-1)';
                        resetProgress(animationCircle);
                        setTimeout(() => {
                            animationCircleContainer.style.transform = null;
                            setProgress(animationCircle, slidesCount, 1);
                        }, Number(window.getComputedStyle(animationCircle).transitionDuration.replace('s', 0) * 1000));
                    } else if (swiper.activeIndex === 0) {
                        animationCircle.style.transitionDuration = `${initialTransitionDuration / 2}s`;
                        resetProgress(animationCircle);
                        setTimeout(() => {
                            animationCircleContainer.style.transform = 'scaleX(-1)';
                            setProgress(animationCircle, slidesCount, slidesCount);
                        }, Number(window.getComputedStyle(animationCircle).transitionDuration.replace('s', 0) * 1000));
                    } else {
                        animationCircleContainer.style.transform = null;
                        animationCircle.style.transitionDuration = null;
                        setProgress(animationCircle, slidesCount, swiper.activeIndex);
                    }
                },
                slideChangeTransitionEnd: swiper => {
                    const context = this.element.closest('.js-stats-carousel');
                    const animationCircleContainer = context.querySelector('.js-stats-carousel-animation svg');
                    const slidesCount = Number(context.querySelector('.js-stats-carousel-animation').dataset.jsStatsCarouselAnimationStepCount);
                    const progressPointOuters = [...animationCircleContainer.querySelectorAll('.js-stats-carousel-animation-progress-point-outer-container circle')];
                    let indexToActivate = swiper.activeIndex;

                    progressPointOuters.forEach(element => element.setAttribute('stroke-opacity', 0));

                    if (swiper.activeIndex === slidesCount) {
                        indexToActivate = 0;
                    }

                    if (swiper.activeIndex === slidesCount + 1) {
                        indexToActivate = 1;
                    }

                    progressPointOuters[indexToActivate].setAttribute('stroke-opacity', 0.4);
                }
            }
        });
    }
}
